import { useEffect, useRef, useState } from 'react';
import { Wordmark, T, F } from '../../lib/bricks';
import { dayLabel, periodLabel, reportNoLabel, visitNo, sinceLine, kindName, investorSinceItems, scaleBar, bytesLabel, numLabel, volumeText, areaText, signedVolumeText } from './progressReportModel';

// The report as a drawing set: a title block, numbered sections, a quiet
// ledger and an evidence index. Black, off-white and graphite only; the mark
// in the wordmark is the one touch of brand orange. Pictures are never
// labelled on top; captions sit under them. Shared by the builder's sheet
// and the public link page.
// Theme-following ink (index.css vars; print always resolves to paper). PIN is the
// fixed ink for white labels drawn over photos and plans.
const P = { ink: 'var(--c-ink)', ink2: 'var(--c-ink-soft)', mute: 'var(--c-ink-mute)', faint: 'var(--c-ink-faint)', hair: 'var(--c-hair-s)', rule: 'var(--c-ink)', well: 'var(--c-bg-soft)' };
const PIN = T.ink;

export const PAPER_CSS = `
.pr-paper{max-width:1040px;margin:0 auto;padding:40px 56px 56px;box-sizing:border-box;color:${P.ink};font-family:${F.display};overflow-wrap:anywhere;font-feature-settings:"tnum" 1}
.pr-paper .pr-mono{font-family:${F.mono};font-variant-numeric:tabular-nums;letter-spacing:0}
.pr-tb{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));border-top:1px solid ${P.rule};border-left:1px solid ${P.hair}}
.pr-tb>div{padding:10px 12px 12px;border-right:1px solid ${P.hair};border-bottom:1px solid ${P.hair};min-width:0}
.pr-stats{display:grid;grid-template-columns:repeat(3,minmax(0,1fr))}
.pr-stats>div{padding:0 16px;border-left:1px solid ${P.hair}}
.pr-stats>div:first-child{padding-left:0;border-left:0}
.pr-pair{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.pr-photos{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:8px}
.pr-photos>div{position:relative;min-width:0}
.pr-photos img{display:block;width:100%;object-fit:cover;background:${P.well}}
.pr-k1{grid-column:span 6}.pr-k1 img{aspect-ratio:5 / 2}
.pr-k2{grid-column:span 3}.pr-k2 img{aspect-ratio:3 / 2}
.pr-k3{grid-column:span 2}.pr-k3 img{aspect-ratio:4 / 3}
.pr-frame{position:relative;overflow:hidden;background:${P.well}}
.pr-credit{margin:4px 0 0;font-size:11.5px;line-height:1.45;color:${P.mute}}
.pr-row{display:grid;grid-template-columns:56px 128px minmax(0,1fr) auto;gap:0 16px;align-items:baseline}
.pr-ev{display:grid;grid-template-columns:44px 112px 40px 120px minmax(0,1fr) auto;gap:0 14px;align-items:baseline;padding:11px 0;border-bottom:1px solid ${P.hair};color:inherit;text-decoration:none}
a.pr-ev:hover .pr-item{text-decoration:underline;text-underline-offset:3px}
.pr-indent{margin-left:216px}
.pr-ev>.pr-sub{display:none}
.pr-src{max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;text-align:right}
.pr-bleed{margin:0}
.pr-ledger{display:grid;grid-template-columns:minmax(120px,200px) minmax(0,1fr);gap:16px;align-items:baseline;padding:12px 0;border-bottom:1px solid ${P.hair}}
.pr-sov{display:grid;grid-template-columns:56px minmax(0,1fr) 120px 110px 120px;gap:0 16px;align-items:baseline;padding:12px 0;border-bottom:1px solid ${P.hair}}
.pr-sov.pr-novalue{grid-template-columns:56px minmax(0,1fr) 110px 120px}
.pr-sov>.pr-sub{display:none}
.pr-fig4{display:grid;grid-template-columns:repeat(4,minmax(0,1fr))}
.pr-fig4>div{padding:0 16px;border-left:1px solid ${P.hair};min-width:0}
.pr-fig4>div:first-child{padding-left:0;border-left:0}
.pr-saved{display:grid;grid-template-columns:56px minmax(0,1fr) 120px 200px;gap:0 16px;align-items:baseline;padding:12px 0;border-bottom:1px solid ${P.hair}}
.pr-iss{display:grid;grid-template-columns:56px minmax(0,1fr) 112px 132px 64px;gap:0 16px;align-items:start;padding:14px 0;border-bottom:1px solid ${P.hair}}
.pr-wx{display:grid;grid-template-columns:56px 128px 170px minmax(0,1fr);gap:0 16px;align-items:baseline;padding:12px 0;border-bottom:1px solid ${P.hair}}
.pr-rev{display:grid;grid-template-columns:56px 128px minmax(0,1fr);gap:0 16px;align-items:baseline;padding:12px 0;border-bottom:1px solid ${P.hair}}
.pr-hash{grid-column:2 / -1;display:flex;gap:10px;align-items:baseline;min-width:0;margin-top:4px}
.pr-hash>.pr-h{min-width:0;overflow-wrap:anywhere;word-break:break-all}
.pr-pin{position:absolute;transform:translate(-5px,-5px);display:flex;align-items:center;gap:4px;pointer-events:none}
.pr-pin>i{display:block;width:10px;height:10px;border-radius:50%;background:#fff;border:2px solid ${PIN};box-sizing:border-box}
.pr-pin>b{font:500 10.5px/1 ${F.mono};background:#fff;color:${PIN};padding:2px 4px}
.pr-tag{position:absolute;left:0;bottom:0;font:500 10.5px/1 ${F.mono};background:#fff;color:${PIN};padding:3px 5px}
@media (max-width:719px){
  .pr-ledger{grid-template-columns:minmax(0,1fr);gap:2px}
  .pr-sov,.pr-sov.pr-novalue{grid-template-columns:40px minmax(0,1fr) auto}
  .pr-sov>.pr-val,.pr-sov>.pr-ev2,.pr-sov>.pr-bar{display:none}
  .pr-sov>.pr-sub{display:block;grid-column:2 / -1}
  .pr-fig4{grid-template-columns:repeat(2,minmax(0,1fr));row-gap:20px}
  .pr-fig4>div:nth-child(3){padding-left:0;border-left:0}
  .pr-saved{grid-template-columns:40px minmax(0,1fr) auto}
  .pr-saved>.pr-map{grid-column:2 / -1}
  .pr-iss{grid-template-columns:40px minmax(0,1fr) auto}
  .pr-iss>.pr-ph{grid-column:2 / 3;grid-row:2;margin-top:10px}
  .pr-iss>.pr-dt{grid-column:2 / -1;grid-row:3;margin-top:6px}
  .pr-iss>.pr-st{grid-column:3;grid-row:1}
  .pr-wx{grid-template-columns:40px minmax(0,1fr)}
  .pr-wx>.pr-wd,.pr-wx>.pr-wh,.pr-wx>.pr-wl{grid-column:2 / -1}
  .pr-rev{grid-template-columns:40px minmax(0,1fr)}
  .pr-rev>.pr-rl{grid-column:2 / -1}
  .pr-hash{grid-column:1 / -1}
  .pr-paper{padding:24px 20px 40px}
  .pr-bleed{margin:0 -20px}
  .pr-tb{grid-template-columns:1fr 1fr}
  .pr-tb>.pr-wide{grid-column:1 / -1}
  .pr-pair{gap:8px}
  .pr-k3{grid-column:span 3}.pr-k3 img{aspect-ratio:3 / 2}
  .pr-k3.pr-i0{grid-column:span 6}.pr-k3.pr-i0 img{aspect-ratio:2 / 1}
  .pr-row{grid-template-columns:40px minmax(0,1fr) auto}
  .pr-row>.pr-cap{grid-column:2 / -1}
  .pr-row-head{display:none}
  .pr-indent{margin-left:56px}
  .pr-ev{grid-template-columns:40px minmax(0,1fr) auto;row-gap:2px}
  .pr-ev>.pr-sub{display:block;grid-column:2 / -1;order:5}
  .pr-src{max-width:118px}
  .pr-ev>.pr-d,.pr-ev>.pr-v,.pr-ev>.pr-k,.pr-ev>.pr-head-only{display:none}
}
`;

const shot = { display: 'block', width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', background: P.well };
const small = { fontSize: 12, color: P.mute };
const monoSmall = { ...small, fontFamily: F.mono, fontSize: 11.5 };

// North arrow: 24px grid, 1.5px stroke, square caps, no fill.
function NorthArrow({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: P.ink2 }} strokeWidth="1.5" strokeLinecap="square" aria-label="North">
      <circle cx="12" cy="13" r="8" />
      <path d="M12 7v10M9 11l3-4 3 4" />
      <path d="M9.75 4V1l4.5 3V1" strokeWidth="1" />
    </svg>
  );
}

function MapCaption({ label, extra, scale }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 8 }}>
      <span className="pr-mono" style={monoSmall}>{[label, extra].filter(Boolean).join(' · ')}</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...small, whiteSpace: 'nowrap' }} data-testid="map-scale-note">
        {scale ? null : 'Not to scale'} <NorthArrow />
      </span>
    </div>
  );
}

// Drawing-set scale bar: two alternating segments, labels under. width is
// the bar's share of the map's width, so it stays true at any size.
function ScaleBar({ bar, px }) {
  return (
    <div style={{ marginTop: 10 }} data-testid="map-scale-bar">
      <div style={{ width: px > 0 ? `${px}px` : `${bar.frac * 100}%`, maxWidth: '100%', minWidth: 0 }}>
        <div style={{ display: 'flex', height: 5, border: `1px solid ${P.ink}`, boxSizing: 'border-box' }}>
          <span style={{ flex: 1, background: P.ink }} /><span style={{ flex: 1, background: 'var(--c-bg)' }} />
        </div>
        <div className="pr-mono" style={{ position: 'relative', height: 16, fontSize: 10.5, color: P.ink2, marginTop: 3 }}>
          <span style={{ position: 'absolute', left: 0 }}>0</span>
          {bar.half ? <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>{bar.half}</span> : null}
          <span style={{ position: 'absolute', right: 0, transform: 'translateX(50%)', whiteSpace: 'nowrap' }}>{bar.label}</span>
        </div>
      </div>
    </div>
  );
}

// Every map sits in the same frame (2:1, full width). A map whose file says
// how wide the ground is, and whose picture is uncropped, shows whole inside
// the frame with a scale bar and photo pins. Otherwise it is cropped to fill
// the frame and marked "Not to scale", with no pins or bar.
export const MAP_FRAME = 2;

// Where a contained map sits in its frame, in % of the frame, so it never waits on a measure.
export function mapBoxPct(nat, ratio = MAP_FRAME) {
  if (!nat || !(nat.w > 0) || !(nat.h > 0)) return null;
  const a = nat.w / nat.h;
  const w = a >= ratio ? 100 : (a / ratio) * 100;
  const h = a >= ratio ? (ratio / a) * 100 : 100;
  return { w, h, left: (100 - w) / 2, top: (100 - h) / 2 };
}

export function mapBox(frameW, nat, ratio = MAP_FRAME) {
  if (!(frameW > 0) || !nat || !(nat.w > 0) || !(nat.h > 0)) return null;
  const frameH = frameW / ratio;
  const a = nat.w / nat.h;
  const w = a >= ratio ? frameW : frameH * a;
  const h = a >= ratio ? frameW / a : frameH;
  return { w, h, left: (frameW - w) / 2, top: (frameH - h) / 2 };
}

export function ReportMap({ map, pins, extra, testid }) {
  const box = useRef(null);
  const [nat, setNat] = useState(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const measure = () => setW(el.clientWidth || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const sc = map.scale;
  const fits = !!(sc && sc.aspect && nat && Math.abs(nat.w / nat.h - sc.aspect) / sc.aspect <= 0.02);
  const inner = fits ? mapBox(w, nat) : null;
  const pct = fits ? mapBoxPct(nat) : null;
  const bar = fits ? scaleBar(sc, inner ? inner.w : w) : null;
  const shown = fits ? (pins || []) : [];
  const alt = `${map.label || 'Map'}${extra ? `, ${extra}` : ''}`;
  const onLoad = (e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
  return (
    <figure style={{ margin: '20px 0 0' }} data-testid={testid}>
      <div className="pr-bleed">
        <div ref={box} className="pr-frame" style={{ aspectRatio: '2 / 1' }} data-testid="map-frame">
          {pct ? (
            <div style={{ position: 'absolute', left: `${pct.left}%`, top: `${pct.top}%`, width: `${pct.w}%`, height: `${pct.h}%` }}>
              <img src={map.src} alt={alt} loading="lazy" onLoad={onLoad} style={{ display: 'block', width: '100%', height: '100%' }} />
              {shown.map((p) => (
                <span key={p.ref} className="pr-pin" style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }} data-testid="map-pin">
                  <i /><b>{p.ref}</b>
                </span>
              ))}
            </div>
          ) : (
            <img src={map.src} alt={alt} loading="lazy" onLoad={onLoad}
              style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
        </div>
      </div>
      {bar ? <ScaleBar bar={bar} px={inner ? bar.frac * inner.w : 0} /> : null}
      <MapCaption label={map.label} extra={extra} scale={!!bar} />
      {bar ? (
        <p style={{ ...small, margin: '4px 0 0', fontSize: 11.5 }}>
          {[sc.source_line, sc.gsd_cm ? `${sc.gsd_cm} cm per pixel` : null, shown.length ? 'Pins are photo GPS positions' : null].filter(Boolean).join('. ')}.
        </p>
      ) : null}
      {map.credit ? <p className="pr-credit" data-testid="map-credit">{map.credit}</p> : null}
    </figure>
  );
}

// Cover picture: the hero, or the next candidate if it doesn't load, or nothing.
// Never an empty frame. A scaled map shows whole, with its scale bar and pins.
function HeroFigure({ view }) {
  const candidates = [view.hero, ...(view.hero_alts || [])].filter((h) => h && h.src);
  const key = candidates.map((h) => h.src).join('|');
  const [at, setAt] = useState(0);
  useEffect(() => { setAt(0); }, [key]);
  const hero = candidates[at];
  if (!hero) return null;
  const extra = [visitNo(hero.visit_no), dayLabel(hero.date)].filter(Boolean).join(' · ');
  if (hero.kind === 'map' && hero.scale) {
    return <div style={{ marginTop: 12 }} data-testid="report-hero"><ReportMap map={hero} pins={hero.pins} extra={extra} /></div>;
  }
  return (
    <figure style={{ margin: '32px 0 0' }} data-testid="report-hero">
      <div className="pr-bleed">
        <img key={hero.src} src={hero.src} alt={`${hero.label || 'Site'}, ${dayLabel(hero.date)}`} style={{ ...shot, aspectRatio: hero.kind === 'map' ? '2 / 1' : '3 / 2' }}
          onError={() => setAt((i) => i + 1)} />
      </div>
      {hero.kind === 'map'
        ? <MapCaption label={hero.label} extra={extra} />
        : <figcaption className="pr-mono" style={{ ...monoSmall, marginTop: 8 }}>{[hero.label, extra].filter(Boolean).join(' · ')}</figcaption>}
      {hero.credit ? <p className="pr-credit">{hero.credit}</p> : null}
    </figure>
  );
}

function Section({ no, title, children, testid }) {
  return (
    <section style={{ marginTop: 64 }} data-testid={testid}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 20, paddingBottom: 14, borderBottom: `1px solid ${P.rule}` }}>
        <span className="pr-mono" style={{ fontSize: 12, color: P.mute }}>{String(no).padStart(2, '0')}</span>
        <h2 style={{ margin: 0, fontSize: 'clamp(24px, 3.2vw, 32px)', lineHeight: 1.1, fontWeight: 300, letterSpacing: '-0.02em' }}>{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Cell({ label, children, mono, wide }) {
  return (
    <div className={wide ? 'pr-wide' : undefined}>
      <div style={{ fontSize: 11.5, color: P.mute }}>{label}</div>
      <div className={mono ? 'pr-mono' : undefined} style={{ fontSize: mono ? 13 : 14, marginTop: 4 }}>{children || '-'}</div>
    </div>
  );
}

function Numbered({ title, prefix, items, testid }) {
  return (
    <div style={{ marginTop: 32 }} data-testid={testid}>
      <div style={{ ...small, paddingBottom: 8, borderBottom: `1px solid ${P.hair}` }}>{title}</div>
      {items && items.length ? items.map((t, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr)', gap: 16, alignItems: 'baseline', padding: '12px 0', borderBottom: `1px solid ${P.hair}` }}>
          <span className="pr-mono" style={monoSmall}>{prefix}{String(i + 1).padStart(2, '0')}</span>
          <span style={{ fontSize: 15 }}>{t}</span>
        </div>
      )) : <p style={{ margin: 0, padding: '12px 0', fontSize: 15, color: P.mute, borderBottom: `1px solid ${P.hair}` }}>None noted.</p>}
    </div>
  );
}

function SubHead({ children, right }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', margin: '32px 0 0', paddingBottom: 8, borderBottom: `1px solid ${P.hair}` }}>
      <span style={{ fontSize: 15, fontWeight: 500 }}>{children}</span>
      {right ? <span className="pr-mono" style={monoSmall}>{right}</span> : null}
    </div>
  );
}

function Ledger({ rows, testid }) {
  return (
    <div data-testid={testid}>
      {rows.filter((r) => r && r[1]).map(([label, value, mono]) => (
        <div key={label} className="pr-ledger">
          <span style={small}>{label}</span>
          <span className={mono ? 'pr-mono' : undefined} style={{ fontSize: mono ? 14 : 15, lineHeight: 1.55 }}>{value}</span>
        </div>
      ))}
    </div>
  );
}

function Caption({ children }) {
  return children ? <p style={{ ...small, margin: '14px 0 0', maxWidth: 640, lineHeight: 1.55 }}>{children}</p> : null;
}

const visitTag = (x) => [x && x.visit_no ? visitNo(x.visit_no) : null, x && x.date ? dayLabel(x.date) : null, x && x.ref].filter(Boolean).join(' · ');

function Milestones({ items, dated }) {
  return (
    <div style={{ marginTop: 32 }} data-testid="report-milestones">
      <div style={{ ...small, display: 'grid', gridTemplateColumns: dated ? '56px minmax(0,1fr) auto' : '56px minmax(0,1fr)', gap: 16, paddingBottom: 8, borderBottom: `1px solid ${P.hair}` }}>
        <span>Milestones</span><span />{dated ? <span>Planned · actual</span> : null}
      </div>
      {items.map((m, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr) auto', gap: '4px 16px', alignItems: 'baseline', padding: '12px 0', borderBottom: `1px solid ${P.hair}` }}>
          <span className="pr-mono" style={monoSmall}>M{String(i + 1).padStart(2, '0')}</span>
          <span style={{ fontSize: 15 }}>{m.text}</span>
          <span style={{ fontSize: 13, color: m.done ? P.ink : P.mute }}>{m.done ? 'Done' : 'Open'}</span>
          {dated && (m.planned || m.actual) ? (
            <span className="pr-mono" style={{ ...monoSmall, gridColumn: '2 / -1' }} data-testid="milestone-dates">
              {[m.planned ? `Planned ${dayLabel(m.planned)}` : null, m.actual ? `Actual ${dayLabel(m.actual)}` : null].filter(Boolean).join(' · ')}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

// Investor update: the builder's schedule and budget, next steps, risks.
function InvestorSection({ no, view }) {
  const inv = view.investor || {};
  return (
    <Section no={no} title="Schedule and budget" testid="report-investor">
      {view.since ? null : <p style={{ margin: '24px 0 0', maxWidth: 640, fontSize: 16, lineHeight: 1.65 }} data-testid="report-since">{sinceLine(view)}</p>}
      {inv.rows && inv.rows.length ? (
        <div style={{ marginTop: 24, borderTop: `1px solid ${P.hair}` }}>
          <Ledger rows={inv.rows.map((r) => [r.label, r.value, r.mono])} />
        </div>
      ) : null}
      <Numbered title="Next steps" prefix="N" items={inv.next_steps} testid="report-next" />
      <Numbered title="Risks" prefix="R" items={inv.risks} testid="report-risks" />
      {inv.figures_note ? <p style={{ ...small, margin: '16px 0 0', maxWidth: 560, lineHeight: 1.5 }}>{inv.figures_note}</p> : null}
    </Section>
  );
}

function SinceSection({ no, view }) {
  const s = view.since;
  const ref = s.report_no ? `No. ${String(s.report_no).padStart(3, '0')}` : 'the last report';
  const items = [...(s.items || []), ...investorSinceItems(view.investor)];
  return (
    <Section no={no} title="Changes since the last report" testid="report-since-section">
      <p style={{ margin: '24px 0 0', fontSize: 16, color: P.ink2 }} data-testid="report-since">{`Since ${ref}, which ended ${dayLabel(s.date_to)}.`}</p>
      <Numbered title="What changed" prefix="C" items={items} testid="report-changes" />
    </Section>
  );
}

function LinesSection({ no, lines }) {
  const hv = !!lines.has_value;
  const cls = `pr-sov${hv ? '' : ' pr-novalue'}`;
  const bar = (pct) => (
    <span style={{ display: 'block', width: 64, height: 2, background: P.hair, marginTop: 6 }}>
      <span style={{ display: 'block', height: 2, width: `${Math.max(0, Math.min(100, pct))}%`, background: P.ink }} />
    </span>
  );
  return (
    <Section no={no} title="Progress by line" testid="report-lines">
      <p style={{ ...small, margin: '16px 0 0', maxWidth: 640, lineHeight: 1.55 }}>Each line's % complete as reported by the builder, with the evidence that shows it. The overall figure in the summary is the builder's own.</p>
      <div className={cls} style={{ ...small, paddingTop: 20 }} aria-hidden="true">
        <span>Line</span><span>Item</span>{hv ? <span className="pr-val">Value</span> : null}<span className="pr-bar">Complete</span><span className="pr-ev2">Evidence</span>
      </div>
      {lines.rows.map((r) => (
        <div key={r.no} className={cls} data-testid="report-line">
          <span className="pr-mono" style={monoSmall}>L{String(r.no).padStart(2, '0')}</span>
          <span style={{ minWidth: 0 }}>
            <span style={{ fontSize: 15 }}>{r.name}</span>
            {r.note ? <span style={{ ...small, display: 'block', marginTop: 4 }}>{r.note}</span> : null}
          </span>
          {hv ? <span className="pr-mono pr-val" style={{ fontSize: 13, color: P.ink2 }}>{r.value_label || '-'}</span> : null}
          <span className="pr-mono" style={{ fontSize: 14 }}>
            {r.percent !== null && r.percent !== undefined ? `${r.percent}%` : '-'}
            <span className="pr-bar">{r.percent !== null && r.percent !== undefined ? bar(r.percent) : null}</span>
          </span>
          <span className="pr-mono pr-ev2" style={monoSmall}>{(r.refs || []).join(', ') || '-'}</span>
          <span className="pr-sub pr-mono" style={monoSmall}>{[hv ? r.value_label : null, (r.refs || []).join(', ')].filter(Boolean).join(' · ')}</span>
        </div>
      ))}
      {hv || lines.weighted_percent !== null ? (
        <div className={cls} style={{ borderBottom: `1px solid ${P.ink}` }} data-testid="report-lines-total">
          <span />
          <span style={{ fontSize: 14, fontWeight: 500 }}>These lines</span>
          {hv ? <span className="pr-mono pr-val" style={{ fontSize: 13 }}>{lines.total_label}</span> : null}
          <span className="pr-mono" style={{ fontSize: 14 }}>{lines.weighted_percent !== null && lines.weighted_percent !== undefined ? `${lines.weighted_percent}%` : '-'}</span>
          <span className="pr-ev2" style={small}>{lines.weighted_percent !== null && lines.weighted_percent !== undefined ? 'weighted by value' : ''}</span>
          <span className="pr-sub" style={small}>{[hv ? lines.total_label : null, lines.weighted_percent !== null && lines.weighted_percent !== undefined ? 'weighted by value' : null].filter(Boolean).join(' · ')}</span>
        </div>
      ) : null}
    </Section>
  );
}

function MeasurementsSection({ no, meas }) {
  const ch = meas.change;
  const saved = meas.saved || [];
  return (
    <Section no={no} title="Measurements" testid="report-measurements">
      {ch ? (
        <div data-testid="report-cut-fill">
          <SubHead right={`${visitTag(ch.from)}  to  ${visitTag(ch.to)}`}>Cut and fill</SubHead>
          <div className="pr-fig4" style={{ marginTop: 20 }}>
            {[['Fill', volumeText(ch.fill_m3)], ['Cut', volumeText(ch.cut_m3)], ['Net', signedVolumeText(ch.net_m3)], ['Area changed', areaText(ch.changed_area_m2)]].map(([k, v]) => (
              <div key={k}>
                <div className="pr-mono" style={{ fontSize: 'clamp(20px, 3vw, 26px)', lineHeight: 1 }}>{v}</div>
                <div style={{ ...small, marginTop: 8 }}>{k}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 20, borderTop: `1px solid ${P.hair}` }}>
            <Ledger rows={[
              ['Method', ch.method],
              ['Scale', ch.scale],
              ['Compared', `${areaText(ch.compared_area_m2)} covered by both clouds${ch.cell_m ? `; ${numLabel(ch.cell_m)} m grid` : ''}${ch.min_change_m ? `; changes under ${numLabel(ch.min_change_m)} m ignored` : ''}`],
              ['Measured', ch.measured_on ? dayLabel(ch.measured_on) : null, true],
            ]} />
          </div>
        </div>
      ) : null}
      {saved.length ? (
        <div data-testid="report-saved-measures">
          <SubHead>Saved on the maps</SubHead>
          {saved.map((m) => (
            <div key={m.no} className="pr-saved">
              <span className="pr-mono" style={monoSmall}>{m.no}</span>
              <span style={{ minWidth: 0 }}>
                <span style={{ fontSize: 15 }}>{[m.kind_label, m.label].filter(Boolean).join(' · ')}</span>
                <span style={{ ...small, display: 'block', marginTop: 4 }}>{`${m.method}. ${m.scale}.`}</span>
              </span>
              <span className="pr-mono" style={{ fontSize: 14 }}>{m.value}</span>
              <span className="pr-mono pr-map" style={monoSmall}>{[m.map, visitTag(m)].filter(Boolean).join(' · ')}</span>
            </div>
          ))}
        </div>
      ) : null}
    </Section>
  );
}

function IssuesSection({ no, issues }) {
  return (
    <Section no={no} title="Issues and observations" testid="report-issues">
      <p style={{ margin: '20px 0 0', fontSize: 15, color: P.ink2 }}>{`${issues.open} open, ${issues.closed} closed.`}</p>
      {issues.rows.map((r) => (
        <div key={r.ref} className="pr-iss" data-testid="report-issue">
          <span className="pr-mono" style={{ fontSize: 12.5, paddingTop: 2 }}>{r.ref}</span>
          <span style={{ minWidth: 0 }}>
            <span style={{ fontSize: 15 }}>{r.title}</span>
            {r.location || r.photo_ref ? <span className="pr-mono" style={{ ...monoSmall, display: 'block', marginTop: 4 }}>{[r.location, r.photo_ref].filter(Boolean).join(' · ')}</span> : null}
            {r.note ? <span style={{ display: 'block', marginTop: 6, fontSize: 13.5, color: P.ink2, lineHeight: 1.5 }}>{r.note}</span> : null}
          </span>
          <span className="pr-ph">
            {r.photo_src ? <img src={r.photo_src} alt={`${r.ref}: ${r.title}`} loading="lazy" style={{ ...shot, width: 112, maxWidth: '100%' }} /> : null}
          </span>
          <span className="pr-mono pr-dt" style={{ ...monoSmall, lineHeight: 1.6 }}>
            {r.opened ? <span style={{ display: 'block' }}>{`Opened ${dayLabel(r.opened)}`}</span> : null}
            {r.closed ? <span style={{ display: 'block' }}>{`Closed ${dayLabel(r.closed)}`}</span> : null}
          </span>
          <span className="pr-st" style={{ fontSize: 13, textAlign: 'right', color: r.status === 'open' ? P.ink : P.mute }}>{r.status_label}</span>
        </div>
      ))}
    </Section>
  );
}

function WeatherSection({ no, w, view }) {
  const rows = w.visits || [];
  const period = w.period || [];
  return (
    <Section no={no} title="Site conditions" testid="report-weather">
      {rows.length ? (
        <>
          <div className="pr-wx pr-row-head" style={{ ...small, paddingTop: 20 }} aria-hidden="true"><span>Visit</span><span>Date</span><span>When</span><span>Conditions</span></div>
          {rows.map((r) => (
            <div key={`${r.visit_no}-${r.date}`} className="pr-wx" data-testid="report-weather-visit">
              <span className="pr-mono" style={{ fontSize: 13 }}>{r.visit_no ? String(r.visit_no).padStart(2, '0') : '-'}</span>
              <span className="pr-mono pr-wd" style={{ fontSize: 13, color: P.ink2 }}>{dayLabel(r.date)}</span>
              <span className="pr-wh" style={{ ...small, fontSize: 12.5 }}>{r.when}</span>
              <span className="pr-wl" style={{ fontSize: 15 }}>{r.line}</span>
            </div>
          ))}
        </>
      ) : null}
      {period.length ? (
        <>
          <div style={{ ...small, margin: '28px 0 0', paddingBottom: 8, borderBottom: `1px solid ${P.hair}` }}>
            {`Over the period (${periodLabel(view.date_from, w.period_through || view.date_to)})`}
          </div>
          <Ledger rows={period.map((p) => [p.label, `${p.value}. ${p.detail}.`])} testid="report-weather-period" />
        </>
      ) : null}
      <Caption>{w.note}</Caption>
      {w.source_line ? (
        <p style={{ ...small, margin: '8px 0 0', maxWidth: 640, lineHeight: 1.55 }} data-testid="report-weather-source">
          {w.source_url ? <a href={w.source_url} target="_blank" rel="noreferrer" style={{ color: 'inherit', textUnderlineOffset: 3 }}>{w.source_line}</a> : w.source_line}
        </p>
      ) : null}
    </Section>
  );
}

function CaptureSection({ no, rows }) {
  const tag = (v, src) => (v && src === 'builder' ? `${v} (builder)` : v);
  return (
    <Section no={no} title="Capture details" testid="report-capture">
      {rows.map((r) => {
        const when = r.time ? `${dayLabel(r.date)} at ${r.time.slice(11, 16)}${r.zone ? ` ${r.zone}` : ''}` : dayLabel(r.date);
        return (
          <div key={r.visit_id} data-testid="report-capture-visit">
            <SubHead right={r.time && r.time_how ? `${when} · from ${r.time_how}` : when}>{visitNo(r.no) || 'Visit'}</SubHead>
            <Ledger rows={[
              ['Aircraft', tag(r.aircraft, r.aircraft_from)],
              ['Height', r.height_m !== null && r.height_m !== undefined ? tag(`${r.height_m} m above takeoff`, r.height_from) : null],
              ['Ground sample distance', r.gsd_cm !== null && r.gsd_cm !== undefined ? `${r.gsd_cm} cm per pixel (orthomosaic)` : null],
              ['Pilot', tag(r.pilot, r.pilot_from)],
              ['Control', tag(r.control, r.control_from)],
            ]} />
          </div>
        );
      })}
      <Caption>From the files' own metadata (photo EXIF, map file), Bricks' records, or entered by the builder where marked. Pilot certificate numbers show the last 4 digits only.</Caption>
    </Section>
  );
}

function RevisionsSection({ no, revs, scope }) {
  return (
    <Section no={no} title={revs.length > 1 ? 'Revisions and scope' : 'Scope'} testid="report-revisions">
      {revs.length > 1 ? (
        <>
          <div className="pr-rev pr-row-head" style={{ ...small, paddingTop: 20 }} aria-hidden="true"><span>Rev</span><span>Date</span><span>Change</span></div>
          {revs.map((r) => (
            <div key={r.rev} className="pr-rev" data-testid="report-revision">
              <span className="pr-mono" style={{ fontSize: 13 }}>{String(r.rev).padStart(2, '0')}</span>
              <span className="pr-mono" style={{ fontSize: 13, color: P.ink2 }}>{r.day || r.at ? dayLabel(r.day || r.at) : '-'}</span>
              <span className="pr-rl" style={{ fontSize: 15 }}>{r.line}</span>
            </div>
          ))}
        </>
      ) : null}
      {scope ? <p style={{ margin: '20px 0 0', maxWidth: 640, fontSize: 14, lineHeight: 1.6, color: P.ink2 }} data-testid="report-scope">{scope}</p> : null}
    </Section>
  );
}

export default function ProgressReportPaper({ view }) {
  if (!view) return null;
  const pct = view.percent_complete;
  const proj = view.project || {};
  const issued = dayLabel(view.prepared_on);
  const rev = Number(view.revision) || 1;
  const ms = view.milestones || [];
  let sec = 0;
  const hasSummary = view.summary || ms.length || (pct !== null && pct !== undefined);
  const lines = view.lines && view.lines.rows && view.lines.rows.length ? view.lines : null;
  const meas = view.measurements && (view.measurements.change || (view.measurements.saved || []).length) ? view.measurements : null;
  const issues = view.issues && view.issues.rows && view.issues.rows.length ? view.issues : null;
  const weather = view.weather && ((view.weather.visits || []).length || (view.weather.period || []).length) ? view.weather : null;
  const capture = view.capture && view.capture.length ? view.capture : null;
  const revs = view.revisions || [];
  const pairs = view.before_after || [];
  return (
    <article className="pr-paper" data-testid="progress-report">
      <style>{PAPER_CSS}</style>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, paddingBottom: 16, borderBottom: `1px solid ${P.hair}` }}>
        <Wordmark size={18} color={P.ink} />
        <span style={{ textAlign: 'right', lineHeight: 1.35 }}>
          <span style={{ display: 'block', ...small }} data-testid="report-kind">{kindName(view)}</span>
          <span className="pr-mono" style={{ display: 'block', fontSize: 12.5 }} data-testid="report-no">{reportNoLabel(view)}{rev > 1 ? ` · Rev ${rev}` : ''}</span>
        </span>
      </header>

      <h1 style={{ margin: '40px 0 0', fontSize: 'clamp(40px, 7vw, 72px)', lineHeight: 1, fontWeight: 300, letterSpacing: '-0.035em' }}>{proj.name || 'Site'}</h1>
      {proj.place ? <p style={{ margin: '14px 0 0', fontSize: 16, color: P.ink2 }}>{proj.place}</p> : null}
      <p className="pr-mono" style={{ margin: '6px 0 0', fontSize: 13, color: P.mute }} data-testid="report-range">{view.range_line}</p>
      {view.title ? <p style={{ margin: '14px 0 0', fontSize: 15, fontWeight: 500 }}>{view.title}</p> : null}

      <HeroFigure view={view} />

      <div className="pr-stats" style={{ marginTop: 40 }}>
        {(view.stats || []).slice(0, 3).map((s) => (
          <div key={s.label} style={{ minWidth: 0 }}>
            <div className="pr-mono" style={{ fontSize: 'clamp(26px, 4vw, 34px)', lineHeight: 1 }}>{s.value}</div>
            <div style={{ ...small, marginTop: 8 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div className="pr-tb" style={{ marginTop: 40 }} data-testid="report-title-block">
        <Cell label="Project" wide>{proj.name}</Cell>
        <Cell label="Address" wide>{proj.place}</Cell>
        <Cell label="Period" mono wide>{periodLabel(view.date_from, view.date_to)}</Cell>
        <Cell label="Prepared for">{view.prepared_for}</Cell>
        <Cell label="Prepared by">{view.prepared_by}</Cell>
        <Cell label={view.kind === 'investor' ? 'Update' : 'Report'} mono>{reportNoLabel(view)}</Cell>
        <Cell label="Captured by">{view.captured_by || '-'}</Cell>
        <Cell label="Issued" mono>{issued}</Cell>
        <Cell label="Revision" mono><span data-testid="report-revision-no">{rev}</span></Cell>
      </div>

      {hasSummary ? (
        <Section no={++sec} title="Summary">
          {pct !== null && pct !== undefined ? (
            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
                <span className="pr-mono" style={{ fontSize: 30 }}>{pct}%</span>
                <span style={small}>complete, as reported by the builder</span>
              </div>
              <div role="progressbar" aria-label="Complete" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} style={{ marginTop: 12, height: 2, background: P.hair }}>
                <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: 2, background: P.ink }} />
              </div>
            </div>
          ) : null}
          {view.summary ? <p style={{ margin: '24px 0 0', maxWidth: 640, fontSize: 16, lineHeight: 1.65, whiteSpace: 'pre-line' }}>{view.summary}</p> : null}
          {ms.length ? <Milestones items={ms} dated={!!view.milestones_dated} /> : null}
        </Section>
      ) : null}

      {view.since ? <SinceSection no={++sec} view={view} /> : null}

      {view.investor ? <InvestorSection no={++sec} view={view} /> : null}

      {lines ? <LinesSection no={++sec} lines={lines} /> : null}

      {pairs.length ? (
        <Section no={++sec} title="Before and after" testid="report-compare">
          {pairs.map((p) => (
            <figure key={p.kind} style={{ margin: '28px 0 0' }}>
              <figcaption style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>{p.label}</figcaption>
              <div className="pr-pair">
                {[['Before', p.before], ['After', p.after]].map(([word, f]) => (
                  <div key={word} style={{ minWidth: 0 }}>
                    <img src={f.src} alt={`${word}: ${p.label}, ${dayLabel(f.date)}`} style={shot} loading="lazy" />
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '2px 8px', marginTop: 8 }}>
                      <span style={{ fontSize: 12.5 }}>{word}</span>
                      <span className="pr-mono" style={monoSmall}>{[visitNo(f.visit_no), dayLabel(f.date)].filter(Boolean).join(' · ')}</span>
                    </div>
                    {f.credit ? <p className="pr-credit">{f.credit}</p> : null}
                  </div>
                ))}
              </div>
            </figure>
          ))}
        </Section>
      ) : null}

      {meas ? <MeasurementsSection no={++sec} meas={meas} /> : null}

      <Section no={++sec} title="Visit ledger" testid="report-visits">
        {!view.visits || !view.visits.length ? <p style={{ margin: '20px 0 0', fontSize: 15, color: P.mute }}>No visits in this date range.</p> : (
          <div className="pr-row pr-row-head" style={{ ...small, padding: '12px 0 8px', borderBottom: `1px solid ${P.hair}` }}>
            <span>Visit</span><span>Date</span><span>Capture</span><span>Status</span>
          </div>
        )}
        {(view.visits || []).map((v) => {
          const refs = v.evidence_refs || [];
          return (
            <div key={v.id} style={{ borderBottom: `1px solid ${P.hair}`, padding: '18px 0 20px' }} data-testid="report-visit">
              <div className="pr-row">
                <span className="pr-mono" style={{ fontSize: 14 }}>{v.no ? String(v.no).padStart(2, '0') : '-'}</span>
                <span className="pr-mono pr-d" style={{ fontSize: 13 }}>{dayLabel(v.date)}</span>
                <span className="pr-cap" style={{ fontSize: 14.5 }}>{[v.capture, v.phase].filter(Boolean).join(' · ')}</span>
                <span style={{ ...small, whiteSpace: 'nowrap' }}>{v.status}</span>
              </div>
              {v.note ? <p className="pr-indent" style={{ marginTop: 10, marginBottom: 0, marginRight: 0, fontSize: 15, lineHeight: 1.6, color: P.ink2, whiteSpace: 'pre-line' }}>{v.note}</p> : null}
              {v.map ? <ReportMap map={v.map} pins={v.pins} extra={dayLabel(v.date)} testid="report-visit-map" /> : null}
              {v.photos && v.photos.length ? (
                <div className="pr-photos" style={{ marginTop: 12 }} data-testid="report-visit-photos">
                  {photoCells(v.photos.length).map(([k, i], n) => { const p = v.photos[n]; return (
                    <div key={n} className={`pr-k${k} pr-i${i}`}>
                      <img src={p.src} alt={p.label || `Photo, ${dayLabel(v.date)}`} loading="lazy" />
                      {p.ref ? <span className="pr-tag">{p.ref}</span> : null}
                    </div>
                  ); })}
                </div>
              ) : null}
              <div className="pr-indent" style={{ ...small, marginTop: 12 }}>
                {v.kinds && v.kinds.length ? v.kinds.join(', ') : 'No files yet for this visit.'}
                {refs.length ? <span className="pr-mono" style={{ fontSize: 11.5 }}>{`  ·  ${refs.join('-')}`}</span> : null}
                {v.more_photos ? ` · ${v.more_photos} more photos in the evidence index` : ''}
              </div>
            </div>
          );
        })}
      </Section>

      {issues ? <IssuesSection no={++sec} issues={issues} /> : null}
      {weather ? <WeatherSection no={++sec} w={weather} view={view} /> : null}
      {capture ? <CaptureSection no={++sec} rows={capture} /> : null}

      <Section no={++sec} title="Evidence index" testid="report-evidence">
        {!view.evidence || !view.evidence.length ? <p style={{ margin: '20px 0 0', fontSize: 15, color: P.mute }}>No files in this date range.</p> : (
          <div className="pr-ev" style={{ ...small, padding: '12px 0 8px' }} aria-hidden="true">
            <span>Ref</span><span className="pr-d">Date</span><span className="pr-v">Visit</span><span className="pr-k">Type</span><span>Item</span><span className="pr-head-only">Source</span>
          </div>
        )}
        {(view.evidence || []).map((e, i) => {
          const href = e.link || e.url;
          const inner = (
            <>
              <span className="pr-mono" style={{ fontSize: 12.5 }}>{e.ref || ''}</span>
              <span className="pr-mono pr-d" style={{ fontSize: 12.5, color: P.ink2 }}>{dayLabel(e.date)}</span>
              <span className="pr-mono pr-v" style={{ fontSize: 12.5, color: P.ink2 }}>{e.visit_no ? String(e.visit_no).padStart(2, '0') : '-'}</span>
              <span className="pr-k" style={{ fontSize: 13, color: P.ink2 }}>{e.kind_label}</span>
              <span className="pr-item" style={{ minWidth: 0, fontSize: 14 }}>{e.label}</span>
              <span className="pr-mono pr-src" style={monoSmall} title={e.file || e.host || undefined} data-testid="evidence-source">{e.file || e.host || ''}</span>
              <span className="pr-sub pr-mono" style={monoSmall}>{[dayLabel(e.date), e.kind_label].filter(Boolean).join(' · ')}</span>
              {e.kind !== 'note' ? (
                <span className="pr-hash pr-mono" style={{ fontSize: 10.5, color: P.mute }} data-testid="evidence-hash">
                  {e.sha256 ? <><span>SHA-256</span><span className="pr-h" style={{ color: P.ink2 }}>{e.sha256}</span>{e.bytes ? <span style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>{bytesLabel(e.bytes)}</span> : null}</> : <span style={{ color: P.faint }}>SHA-256 pending</span>}
                </span>
              ) : null}
            </>
          );
          return href
            ? <a key={i} className="pr-ev" href={href} target="_blank" rel="noreferrer">{inner}</a>
            : <div key={i} className="pr-ev">{inner}</div>;
        })}
        {view.evidence && view.evidence.length ? <Caption>{evidenceCaption(view)}</Caption> : null}
        {view.imagery_credits && view.imagery_credits.length ? <Caption>{`Map imagery: ${view.imagery_credits.join('; ')}`.replace(/([^.])$/, '$1.')}</Caption> : null}
      </Section>

      {revs.length > 1 || view.scope_line ? <RevisionsSection no={++sec} revs={revs} scope={view.scope_line} /> : null}

      <footer style={{ marginTop: 64, paddingTop: 14, borderTop: `1px solid ${P.hair}`, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, ...small }}>
        <span>Issued <span className="pr-mono">{issued}</span> · Rev {rev}</span>
        <span>{view.credit || 'Captured and delivered by Bricks'} · brxs.ca</span>
      </footer>
    </article>
  );
}

// Photos in rows that fill the width: 1-3 in one row, 4 as 2+2, 5 as 3+2,
// 6 as 3+3. Returns [photos in its row, place in row] for each photo.
export function photoRowSizes(n) {
  if (!(n > 0)) return [];
  if (n <= 3) return [n];
  if (n === 4) return [2, 2];
  const rows = Array(Math.floor(n / 3)).fill(3);
  if (n % 3 === 1) { rows[rows.length - 1] = 2; rows.push(2); } else if (n % 3 === 2) rows.push(2);
  return rows;
}

export function photoCells(n) {
  const out = [];
  photoRowSizes(n).forEach((k) => { for (let i = 0; i < k; i += 1) out.push([k, i]); });
  return out;
}

function evidenceCaption(view) {
  const ev = view.evidence || [];
  const files = ev.filter((e) => e.kind !== 'note').length;
  const hashed = ev.filter((e) => e.sha256).length;
  const bits = [];
  if (files) bits.push(`SHA-256 of each file as stored on ${dayLabel(view.prepared_on)}${hashed < files ? `; ${files - hashed} still pending` : ''}.`);
  bits.push(view.evidence_linked ? "Each row opens the file through this report's share link while it is active." : 'Share the report to give readers working links to each file.');
  return bits.join(' ');
}
